import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSiteConfig } from '@/lib/siteConfig';
import { getUser } from '@/lib/auth';
import Logo from '@/components/Logo';

const TONES = {
  info: 'bg-indigo-600 text-white',
  success: 'bg-emerald-600 text-white',
  warning: 'bg-amber-500 text-gray-900',
};

const MaintenancePage = ({ message, eta, onRetry }) => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-6">
    <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
      <div className="flex justify-center mb-5"><Logo size="lg" withText={false} /></div>
      <div className="text-4xl mb-3">🛠️</div>
      <h1 className="text-2xl font-bold text-gray-800 mb-2">We’ll be right back</h1>
      <p className="text-gray-600 mb-4">{message || 'DotLink is getting a quick upgrade.'}</p>
      {eta && <p className="text-sm font-semibold text-indigo-600 mb-4">Expected back: {eta}</p>}
      <button onClick={onRetry} className="btn-press bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full px-8 py-3 font-semibold">Try again</button>
      <p className="mt-6 text-xs text-gray-400"><Link to="/login" className="hover:underline">Admin sign in</Link></p>
    </div>
  </div>
);

// Wraps every route: announcement bar on top, maintenance page instead of the app
// while maintenance mode is on (admins, /login and /admin always get through, and
// finder pages stay up when "keep QR scans & calls working" is ticked).
const SiteGate = ({ children }) => {
  const { config, refresh } = useSiteConfig();
  const { pathname } = useLocation();
  const m = config.maintenance;
  const a = config.announcement;
  const isAdmin = Boolean(getUser()?.is_admin);
  const open = pathname.startsWith('/login') || pathname.startsWith('/admin');
  const finderOpen = m.keep_finder_working && (pathname.startsWith('/scan/') || pathname.startsWith('/public/'));

  if (m.enabled && !isAdmin && !open && !finderOpen) {
    return <MaintenancePage message={m.message} eta={m.eta} onRetry={refresh} />;
  }
  return (
    <>
      {m.enabled && isAdmin && (
        <div className="bg-red-600 text-white text-center text-xs font-semibold py-1.5">
          Maintenance mode is ON — visitors see the maintenance page. <Link to="/admin" className="underline">Manage</Link>
        </div>
      )}
      {a.enabled && !pathname.startsWith('/admin') && (
        <div className={`${TONES[a.tone] || TONES.info} text-center text-sm font-medium px-4 py-2`}>
          {a.text}{' '}
          {a.link_url && (a.link_url.startsWith('/')
            ? <Link to={a.link_url} className="underline font-semibold">{a.link_label || 'Learn more'}</Link>
            : <a href={a.link_url} className="underline font-semibold" target="_blank" rel="noopener noreferrer">{a.link_label || 'Learn more'}</a>)}
        </div>
      )}
      {children}
    </>
  );
};

export default SiteGate;
